import React from 'react';
import clsx from 'clsx';

import Button from '@components/button/Button';

import theme from '../../../themes/colors';

interface FloatingBarAction {
  label: string;
  iconName?: string;
  disabled?: boolean;
  id: string | number;
  leftIconStyle?: string;
  buttonClassName?: string;
}

interface FloatingBottomBarProps {
  visible?: boolean;
  selectedCount?: number;
  selectedLabel?: string;
  message?: string;
  actions: FloatingBarAction[]; // Up to 3 will be rendered
  onClose?: () => void;
  className?: string;
  style?: React.CSSProperties;
  testId?: string;
  handleActionClick: (id: string | number) => void;
}

const FloatingBottomBar: React.FC<FloatingBottomBarProps> = ({
  visible = true,
  selectedCount,
  selectedLabel = 'selected',
  message,
  actions,
  onClose,
  className,
  style,
  testId,
  handleActionClick
}) => {
  if (!visible) return null;

  // allow only first 3 actions
  const actionsToRender = (actions || []).slice(0, 3);

  return (
    <div
      className={clsx(
        'fixed bottom-12 left-1/2 z-50 -translate-x-1/2',
        className
      )}
      style={style}
      data-testid={testId || 'floating-bottom-bar'}
      role="region"
      aria-label="Floating action bar"
    >
      <div className="flex h-12 items-center gap-[7px] rounded-xl bg-content px-2 shadow-lg">
        {!!onClose && (
          <Button
            variant="transparent"
            size="none"
            handleClick={onClose}
            leftIconName="close-red"
            labelStyle="text-[12px] font-medium !text-grey40"
            leftIconStroke="#ffffff"
            leftIconStyle="!w-3 !h-3 !mr-0 !min-w-3"
            className={clsx('!h-4 !w-4 p-1')}
          />
        )}

        {(typeof selectedCount === 'number' || message) && (
          <div className="flex items-center gap-2 py-2">
            {typeof selectedCount === 'number' && (
              <span className="rounded-md py-1 text-xs font-medium text-grey40">
                {selectedCount} {selectedLabel}
              </span>
            )}

            <div className="mx-1 h-7.5 w-px bg-[#454545]" />

            {message && (
              <span className="text-xs font-medium text-grey40">{message}</span>
            )}
          </div>
        )}

        {actionsToRender.length > 0 && (
          <div className="flex items-center gap-2">
            {actionsToRender.map((action) => (
              <Button
                key={String(action.id || action.label)}
                variant="transparent"
                size="none"
                label={action.label}
                handleClick={() => handleActionClick(action.id)}
                disabled={action.disabled}
                leftIconName={action.iconName || ''}
                labelStyle="!text-[12px] font-medium !text-grey40"
                leftIconStroke={theme.grey40}
                leftIconStyle={action.leftIconStyle}
                className={clsx(
                  action.buttonClassName,
                  '!h-7.5 rounded-md border border-[#454545] p-1.5'
                )}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default FloatingBottomBar;
